import { useState, useEffect, useContext } from "react";
import MainStateContext from "../state/mainStateContext";
import { applePayMerchantValidation } from "./services/applePayMerchantValidation";
import type { ApplePayButton, ApplePayPaymentResponse } from "../types/applepayTypes";
import "./styles.css";
import { triggerEvent, triggerEventOnce, wvusLogger } from "../helpers";

const ApplePayButton: React.FC<ApplePayButton> = ({ setOrderPayload, hasProduct }) => {
  const [total, setTotal] = useState("0.00");
  const [lineItems, setLineItems] = useState([]);
  const { state, toggleOverlay, setSubmitMessagePhraseKey } = useContext(MainStateContext);
  const { products } = state;

  const validateApplePayMerchantSession = async (session: ApplePaySession) => {
    const validationResult = await applePayMerchantValidation();
    if (validationResult.success) {
      session.completeMerchantValidation(validationResult.data);
    } else {
      setSubmitMessagePhraseKey("errors.alt_pay_error");
      wvusLogger.log(
        "validateApplePayMerchantSession: Error validating ApplePay merchant session ",
        wvusLogger.getLogLevelNames().ERROR
      );
      session.abort();
    }
  };

  const handlePaymentAuthorization = (
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    session: ApplePaySession
  ) => {
    const orderDetails: ApplePayPaymentResponse = {
      billingAddress: {
        city: event.payment.billingContact.locality,
        address1: event.payment.billingContact.addressLines[0],
        address2: event.payment.billingContact.addressLines[1],
        postcode: event.payment.billingContact.postalCode,
        state: event.payment.billingContact.administrativeArea,
        email: event.payment.shippingContact.emailAddress,
        firstName: event.payment.shippingContact.givenName,
        lastName: event.payment.shippingContact.familyName,
        phone: event.payment.shippingContact.phoneNumber
      },
      paymentInfo: {
        pkToken: JSON.stringify(event.payment.token),
        paymentMethod: "WP_ApplePay"
      }
    };

    setOrderPayload(orderDetails, (orderData: { orderId?: string }) => {
      if (orderData?.orderId) {
        toggleOverlay(true);
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
      } else {
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    });
  };

  const processPayment = () => {
    const session: ApplePaySession = new ApplePaySession(3, {
      countryCode: "US",
      currencyCode: "USD",
      total: {
        label: "Total",
        amount: total
      },
      lineItems,
      merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
      supportedNetworks: ["visa", "masterCard", "amex", "discover", "jcb"],
      requiredBillingContactFields: ["postalAddress", "name"],
      requiredShippingContactFields: ["name", "phone", "email"]
    } as ApplePayJS.ApplePayPaymentRequest);

    session.onvalidatemerchant = () => {
      validateApplePayMerchantSession(session);
    };

    session.onpaymentauthorized = (event) => {
      handlePaymentAuthorization(event, session);
    };

    session.begin();
  };

  useEffect(() => {
    let total = "0.00";

    const parsedLineItems = products.map((product) => {
      const productPrice = product?.price || 39;
      total = (parseFloat(productPrice.toString()) + parseFloat(total)).toFixed(2).toString();

      return {
        label: product?.name || "Child Sponsorship",
        type: "final",
        amount: (product?.price || 39).toFixed(2),
        paymentTiming: product?.frequency === "onetime" ? "immediate" : "recurring",
        recurringPaymentStartDate: new Date(),
        recurringPaymentIntervalUnit: product?.frequency === "yearly" ? "year" : "month",
        recurringPaymentIntervalCount: 1
      };
    });

    setLineItems(parsedLineItems);
    setTotal(total);
  }, [products]);

  useEffect(() => {
    // Only trigger the event after the product has been added.
    if (hasProduct) {
      triggerEventOnce("alt_pay_view_applepay", { products });
    }
  }, [products, hasProduct]);

  useEffect(() => {
    const button = document.querySelector("apple-pay-button");
    const handleClick = () => {
      triggerEventOnce("add_to_cart", { products });
      triggerEvent("begin_checkout_alt_pay", { products, eventLabel: "applepay" });
      triggerEventOnce("begin_checkout", { products });
      processPayment();
    };

    if (button) {
      button.addEventListener("click", handleClick);
    }

    return () => {
      if (button) {
        button.removeEventListener("click", handleClick);
      }
    };
  }, [processPayment, products]);

  return (
    <div className={`wp-applepay-wrapper ${!hasProduct ? "disabled" : ""}`}>
      <apple-pay-button buttonstyle="white-outline" type="plain" locale="en" />
    </div>
  );
};

export default ApplePayButton;
export { ApplePayButton };
